import type { ChatMessage } from '../client/types';
import type { PendingGenerativeUiComponent } from '../components/generative-ui';

/** The wire shape of `ChatMessage.component` — assistant-only, and only when present. */
export type RawGenerativeUiComponent = ChatMessage['component'];

type ComponentBearingMessage = {
    role: ChatMessage['role'];
    component?: RawGenerativeUiComponent;
};

/**
 * The single source for "is there an unanswered generative-UI question right now?" —
 * derived from `component` on whichever message is currently last in the thread,
 * whether that message came from loaded history or a just-settled send. Both callers
 * feed it the same shape so a reload and a live turn resolve identically.
 *
 * Only an assistant message can be pending: a customer message afterwards (typed or a
 * component reply) always becomes the new last message and this returns null for it.
 */
export function derivePendingComponent(
    lastMessage: ComponentBearingMessage | undefined
): PendingGenerativeUiComponent | null {
    if (lastMessage === undefined || lastMessage.role !== 'assistant') {
        return null;
    }

    const component = lastMessage.component;
    if (component === undefined) {
        return null;
    }

    const { type, data } = component;
    if (typeof type !== 'string' || type === '') {
        return null;
    }

    return { type, data: data ?? {} };
}
